import {formatAmount} from "./format";
import {rublesToKopecks} from "./amount";

/**
 * Условия входящей заявки кабинета: диапазон суммы, число переводов и минимум
 * одного. Здесь только чистые функции без React: их пользуются форма мерчанта,
 * карточки заявок и редактор реквизитов трейдера, и считают они одинаково.
 *
 * Все суммы в функциях — целые копейки; рубли появляются только в текстах.
 */

/** Столько частей допускает выдача реквизитов (как на сервере). */
export const MAX_PORTAL_IN_TRANSFERS = 20;

export type PortalInAmountMode = "exact" | "range";

/** Что мерчант набрал в форме про сумму и переводы; суммы — рубли строкой. */
export interface PortalInTermsValues {
  amountMode: PortalInAmountMode;
  /** Точная сумма, а при диапазоне — его верхняя граница («до»). */
  amount: string;
  /** Нижняя граница («от»); читается только при диапазоне. */
  amountMin: string;
  transferCount: string;
  /** Минимум одного перевода; читается только при двух переводах и больше. */
  minTransfer: string;
}

/** Условия, как их принимает API (копейки); пустое поле — условия нет. */
export interface PortalInTerms {
  amountMin?: number;
  transferCount?: number;
  minTransferAmount?: number;
}

export type PortalInTermsField = "amount" | "amountMin" | "transferCount" | "minTransfer";
export type PortalInTermsErrors = Partial<Record<PortalInTermsField, string>>;

const WHOLE_PATTERN = /^\d+$/;

function pluralForm(count: number, one: string, few: string, many: string): string {
  const tail = Math.abs(count) % 100;
  if (tail >= 11 && tail <= 14) return many;
  switch (Math.abs(count) % 10) {
    case 1:
      return one;
    case 2:
    case 3:
    case 4:
      return few;
    default:
      return many;
  }
}

/** «перевод», «перевода», «переводов» для числа. */
export function transfersWord(count: number): string {
  return pluralForm(count, "перевод", "перевода", "переводов");
}

/** Рубли для текста: целые без копеек («50 000»), иначе с копейками («33 333,33»). */
export function formatRubles(kopecks: number): string {
  if (kopecks % 100 === 0) {
    return (kopecks / 100).toLocaleString("ru-RU", {maximumFractionDigits: 0});
  }
  return formatAmount(kopecks);
}

/** Целое число переводов 1..20; всё остальное — null. */
export function parseTransferCount(raw: string): number | null {
  const text = raw.trim();
  if (!WHOLE_PATTERN.test(text)) return null;
  const count = Number(text);
  return count >= 1 && count <= MAX_PORTAL_IN_TRANSFERS ? count : null;
}

/**
 * Нижняя граница выбора суммы: граница диапазона, поднятая минимумом одного
 * перевода — даже одним реквизитом меньше него не выдать. Количество переводов —
 * верхний предел: трейдер может выдать и меньше реквизитов, поэтому границу оно
 * не поднимает. Без переводов — сама граница диапазона.
 */
export function choosableLow(rangeLow: number, count: number | null, minTransfer: number | null): number {
  if (count !== null && count >= 2 && minTransfer !== null) return Math.max(rangeLow, minTransfer);
  return rangeLow;
}

function moneyProblem(raw: string): string {
  const text = raw.trim();
  if (!text) return "";
  if (rublesToKopecks(text) === null) {
    return /^[\d\s]+([.,]\d{0,2})?$/.test(text) && Number(text.replace(/\s/g, "").replace(",", ".")) === 0
      ? "Сумма должна быть больше нуля."
      : "Сумма — число с не более чем двумя знаками после запятой.";
  }
  return "";
}

/**
 * Проверки диапазона и переводов в форме. Ошибки самой суммы («до» или точной)
 * остаются за формой: сюда она попадает уже проверенной, и если сумма не
 * разобралась, проверки, которым нужна её граница, пропускаются.
 */
export function validatePortalInTerms(values: PortalInTermsValues): PortalInTermsErrors {
  const errors: PortalInTermsErrors = {};
  const upper = rublesToKopecks(values.amount);
  const range = values.amountMode === "range";

  if (range) {
    const raw = values.amountMin.trim();
    if (!raw) {
      errors.amountMin = "Укажите нижнюю границу диапазона.";
    } else {
      const lower = rublesToKopecks(raw);
      if (lower === null) {
        errors.amountMin = moneyProblem(raw);
      } else if (upper !== null && lower >= upper) {
        errors.amountMin = "Нижняя граница должна быть меньше верхней.";
      }
    }
  }

  const countText = values.transferCount.trim();
  if (!countText) {
    errors.transferCount = "Укажите количество переводов — от 1 до 20.";
    return errors;
  }
  const count = parseTransferCount(countText);
  if (count === null) {
    errors.transferCount = "Количество переводов — целое число от 1 до 20.";
    return errors;
  }
  if (count < 2) return errors;

  const minText = values.minTransfer.trim();
  if (!minText) {
    errors.minTransfer = "Укажите минимальную сумму одного перевода.";
    return errors;
  }
  const minTransfer = rublesToKopecks(minText);
  if (minTransfer === null) {
    errors.minTransfer = moneyProblem(minText);
    return errors;
  }
  if (upper !== null && count * minTransfer > upper) {
    errors.minTransfer = `${count} ${transfersWord(count)} по ${formatRubles(minTransfer)} ₽ — это ${formatRubles(count * minTransfer)} ₽, больше ${
      range ? "верхней границы диапазона" : "суммы заявки"
    } ${formatRubles(upper)} ₽.`;
  }
  return errors;
}

/**
 * Условия для тела запроса. Один перевод и точная сумма — пустой объект: поля
 * не отправляются, заявка создаётся как раньше. null — значения не разобрались
 * (форма до этого не должна дойти).
 */
export function toPortalInTerms(values: PortalInTermsValues): PortalInTerms | null {
  const terms: PortalInTerms = {};
  if (values.amountMode === "range") {
    const amountMin = rublesToKopecks(values.amountMin);
    if (amountMin === null) return null;
    terms.amountMin = amountMin;
  }
  const count = parseTransferCount(values.transferCount);
  if (count === null) return null;
  if (count >= 2) {
    const minTransferAmount = rublesToKopecks(values.minTransfer);
    if (minTransferAmount === null) return null;
    terms.transferCount = count;
    terms.minTransferAmount = minTransferAmount;
  }
  return terms;
}

/**
 * Подсказка под полями: что увидит трейдер. Пустая строка — подсказывать
 * нечего (точная сумма, один перевод) или поля ещё не разобрались.
 */
export function portalInTermsHint(values: PortalInTermsValues): string {
  const upper = rublesToKopecks(values.amount);
  const count = parseTransferCount(values.transferCount);
  if (upper === null || count === null) return "";
  const minTransfer = count >= 2 ? rublesToKopecks(values.minTransfer) : null;
  if (count >= 2 && minTransfer === null) return "";
  // Подсказка не спорит с ошибкой: минимумы, не помещающиеся в сумму, подсказки не получают.
  if (minTransfer !== null && count * minTransfer > upper) return "";
  // «до N реквизитов» — родительный падеж, при N от 2 до 20 всегда «реквизитов».
  const transfers = count >= 2 && minTransfer !== null
    ? `выдаст от 1 до ${count} реквизитов, каждый не меньше ${formatRubles(minTransfer)} ₽`
    : "";

  if (values.amountMode === "range") {
    const lower = rublesToKopecks(values.amountMin);
    if (lower === null || lower >= upper) return "";
    const low = choosableLow(lower, count, minTransfer);
    if (low > upper) return "";
    const choice = `Трейдер выберет сумму от ${formatRubles(low)} до ${formatRubles(upper)} ₽`;
    return transfers ? `${choice} и ${transfers}.` : `${choice}.`;
  }
  return transfers ? `Трейдер ${transfers}, вместе ровно ${formatRubles(upper)} ₽.` : "";
}

/** Суммы мерчантского API — рубли строкой («50000»); для текста приводим их к виду «50 000». */
export function formatPortalRubles(raw: string): string {
  const value = Number(raw.replace(/\s/g, "").replace(",", "."));
  if (!Number.isFinite(value)) return raw;
  return formatRubles(Math.round(value * 100));
}

interface MerchantTermsSource {
  amountMin?: string | null;
  amountMax?: string | null;
  amountChosenAt?: string | null;
  transferCount?: number | null;
  minTransferAmount?: string | null;
}

/**
 * Сумма заявки в карточке и списке мерчанта. Пока трейдер не выбрал сумму из
 * диапазона, заявка стоит «от и до»; после выбора и у заявок без диапазона —
 * просто сумма (`amount`).
 */
export function merchantAmountText(item: MerchantTermsSource & {amount: string; currency: string}): string {
  if (item.amountMin && item.amountMax && !item.amountChosenAt) {
    return `${formatPortalRubles(item.amountMin)}–${formatPortalRubles(item.amountMax)} ${item.currency}`;
  }
  return `${item.amount} ${item.currency}`;
}

/**
 * Строки условий в карточке и списке мерчанта. Пустой массив — у заявки нет
 * ни диапазона, ни переводов, и показывать нечего.
 */
export function merchantTermsLines(item: MerchantTermsSource): string[] {
  const lines: string[] = [];
  if (item.amountMin && item.amountMax) {
    lines.push(
      item.amountChosenAt
        ? `Диапазон ${formatPortalRubles(item.amountMin)}–${formatPortalRubles(item.amountMax)} ₽`
        : "Точную сумму выберет трейдер",
    );
  }
  if (item.transferCount && item.transferCount >= 2 && item.minTransferAmount) {
    lines.push(`До ${item.transferCount} переводов, каждый от ${formatPortalRubles(item.minTransferAmount)} ₽`);
  }
  return lines;
}

/** Что про условия заявки приходит трейдеру в карточке платежа. */
export interface TraderPayinTermsSource {
  payinAmountMin?: number | null;
  payinAmountMax?: number | null;
  payinAmountChosenAt?: string | null;
  payinTransferCount?: number | null;
  payinMinTransferAmount?: number | null;
}

export interface TraderPayinTerms {
  /** Диапазон, из которого сумму ещё предстоит выбрать; null — выбирать нечего. */
  range: {min: number; max: number} | null;
  /** Диапазон мерчанта, когда сумма из него уже выбрана. */
  chosenFrom: {min: number; max: number} | null;
  /** Сколько реквизитов можно выдать самое большее (2..20); null — число не задано. */
  transferCount: number | null;
  /** Минимум одного перевода; задан вместе с числом переводов. */
  minTransfer: number | null;
}

/** Условия заявки для трейдера; null — у заявки нет ни диапазона, ни числа переводов. */
export function traderPayinTerms(item: TraderPayinTermsSource): TraderPayinTerms | null {
  const min = item.payinAmountMin ?? null;
  const max = item.payinAmountMax ?? null;
  const hasRange = min !== null && max !== null;
  const chosen = Boolean(item.payinAmountChosenAt);
  const count = item.payinTransferCount ?? null;
  const minTransfer = item.payinMinTransferAmount ?? null;
  const hasTransfers = count !== null && count >= 2 && minTransfer !== null;
  if (!hasRange && !hasTransfers) return null;
  return {
    range: hasRange && !chosen ? {min, max} : null,
    chosenFrom: hasRange && chosen ? {min, max} : null,
    transferCount: hasTransfers ? count : null,
    minTransfer: hasTransfers ? minTransfer : null,
  };
}

/**
 * Из чего трейдер выбирает сумму: нижняя граница поднята минимумом одного
 * перевода, потому что меньше выдать не получится. null — выбирать нечего
 * или выбор пуст.
 */
export function selectableRange(terms: TraderPayinTerms): {low: number; high: number} | null {
  if (!terms.range) return null;
  const low = choosableLow(terms.range.min, terms.transferCount, terms.minTransfer);
  return low > terms.range.max ? null : {low, high: terms.range.max};
}

/**
 * Сумма заявки в шапке и строке списка трейдера, пока её не выбрали:
 * «52 000,00–55 000,00» — из чего можно выбрать. null — сумма уже известна.
 */
export function traderAmountRangeText(item: TraderPayinTermsSource): string | null {
  const terms = traderPayinTerms(item);
  const bounds = terms ? selectableRange(terms) : null;
  return bounds ? `${formatAmount(bounds.low)}–${formatAmount(bounds.high)}` : null;
}

/** Что не так с выбранной суммой; пустая строка — сумма подходит. */
export function orderAmountProblem(terms: TraderPayinTerms, kopecks: number | null): string {
  const bounds = selectableRange(terms);
  if (!bounds) return "";
  if (kopecks === null) return "Укажите сумму заявки.";
  if (kopecks < bounds.low || kopecks > bounds.high) {
    return `Сумма вне диапазона: от ${formatRubles(bounds.low)} до ${formatRubles(bounds.high)} ₽.`;
  }
  return "";
}

/**
 * Делит сумму на равные части: копейки округляются вниз, остаток уходит в
 * последнюю часть (100 000 ₽ на три — 33 333,33 + 33 333,33 + 33 333,34).
 */
export function splitEqually(total: number, count: number): number[] {
  const share = Math.floor(total / count);
  return Array.from({length: count}, (_, index) => (index === count - 1 ? total - share * (count - 1) : share));
}

/** Часть меньше минимума, который просит мерчант. */
export function belowTransferMinimum(terms: TraderPayinTerms | null, amount: number): boolean {
  return terms?.minTransfer != null && amount > 0 && amount < terms.minTransfer;
}

/**
 * Выполнены ли условия по числу и размеру частей: один реквизит на всю сумму
 * или не больше частей, чем разрешил мерчант, и каждая не меньше минимума.
 * Сумму частей и выбранную сумму проверяют другие функции: здесь только то,
 * что просит мерчант.
 */
export function transferTermsMet(terms: TraderPayinTerms | null, parts: ReadonlyArray<{amount: number}> | undefined): boolean {
  if (!terms || terms.transferCount === null) return true;
  if (!parts?.length) return true;
  if (parts.length > terms.transferCount) return false;
  return parts.every((part) => terms.minTransfer === null || part.amount >= terms.minTransfer);
}

/**
 * Сколько реквизитов трейдер может выдать на сумму `total`: не больше, чем
 * разрешил мерчант, и столько, чтобы каждой части хватило минимума. Без условий
 * мерчанта — общий предел частей.
 */
export function transferLimit(terms: TraderPayinTerms | null, total: number): number {
  if (!terms || terms.transferCount === null) return MAX_PORTAL_IN_TRANSFERS;
  if (terms.minTransfer === null || terms.minTransfer <= 0) return terms.transferCount;
  return Math.max(1, Math.min(terms.transferCount, Math.floor(total / terms.minTransfer)));
}

/** Строки условий в карточке платежа трейдера; пустой массив — условий нет. */
export function traderTermsLines(terms: TraderPayinTerms | null): string[] {
  if (!terms) return [];
  const lines: string[] = [];
  if (terms.range) {
    lines.push(`Мерчант просит сумму от ${formatRubles(terms.range.min)} до ${formatRubles(terms.range.max)} ₽, выберите её при выдаче реквизитов.`);
  } else if (terms.chosenFrom) {
    lines.push(`Сумма выбрана из диапазона ${formatRubles(terms.chosenFrom.min)}–${formatRubles(terms.chosenFrom.max)} ₽.`);
  }
  if (terms.transferCount !== null && terms.minTransfer !== null) {
    lines.push(`Мерчант может оплатить до ${terms.transferCount} переводов, каждый не меньше ${formatRubles(terms.minTransfer)} ₽.`);
  }
  return lines;
}
